/**
 * Help mode: the console explaining itself, in place.
 *
 * WHAT THIS IS. One toggle in the top bar. When it is on, every screen
 * grows plain-English notes where they are needed: a short paragraph at
 * the top saying what the screen is for and what the signed-in person's
 * role can do here, and a one-line note under each control whose effect
 * on the building is not obvious from its label.
 *
 * WHAT IT IS DELIBERATELY NOT. Not a tour. It never navigates, never
 * clicks anything on the user's behalf, never opens a drawer and never
 * covers the page. It renders text into the layout that is already
 * there. That choice is the whole safety story: at Hotel NoMad the
 * controls under discussion arm lift banks and open doors, and a
 * training layer that drives the UI is a training layer that can cause
 * an incident. It also sidesteps this codebase's own recorded trap --
 * `body { pointer-events: none }` in `styles/global.css` has twice
 * shipped a body-portalled layer that painted correctly and ignored
 * every click. Help mode portals nothing.
 *
 * WHERE THE WORDS LIVE. In `i18n/locales/en.ts` under `help.`, never in
 * the components. Akash's answer on 2026-09-06 was "English now, built
 * to translate later", so a second language must be a translation file
 * rather than a rewrite of thirteen screens. This is the first
 * load-bearing use of that scaffold.
 *
 * Non-component exports live here so React Fast Refresh can re-render
 * the provider without invalidating them -- the same split `i18n/`
 * makes for the same reason.
 */
import { createContext } from 'react';
import { helpSeverity } from '../i18n/locales/help.en';

export interface HelpModeValue {
  /** Is Help mode showing right now. */
  enabled: boolean;
  /** Has this person ever turned it on, on this browser. */
  seen: boolean;
  setEnabled: (next: boolean) => void;
  toggle: () => void;
}

export const HelpModeContext = createContext<HelpModeValue | null>(null);

/**
 * How loud a note is. It is CODE, not copy, because it decides colour
 * and an icon and must not be lost or softened in translation -- a
 * translator may render the words of a danger note gently, but the note
 * still has to look like a danger note.
 *
 * plain   -- explains something. No consequence to get wrong.
 * caution -- starts real work, or costs time to undo.
 * danger  -- irreversible, or it acts on the building: doors, lifts,
 *            the whole site's emergency state.
 */
export type HelpSeverity = 'plain' | 'caution' | 'danger';

/**
 * `id -> severity` for every note that is not `plain`. Absent means
 * plain, so the map only carries what matters.
 *
 * GENERATED from the `(caution)` / `(danger)` tag beside each note in
 * `docs/help-copy/*.md`, so the person editing the words also sets how
 * loudly they are shown -- but the value lands in CODE rather than in
 * the message, which is what stops a translation softening a warning
 * about a lift bank into a passing remark. Every entry is a control
 * that can change the hotel: adding one is cheap, removing one is a
 * claim that an action became safe.
 */
export const HELP_SEVERITY: Readonly<Record<string, HelpSeverity>> = helpSeverity;

export function severityOf(id: string): HelpSeverity {
  return HELP_SEVERITY[id] ?? 'plain';
}
